/**
 * Thin fetch wrapper for the TocynID API.
 *
 * Responsibilities:
 *  - Prepend VITE_API_BASE (or /api prefix in dev via Vite proxy)
 *  - Send credentials so session cookies flow
 *  - Parse JSON and throw typed errors
 *  - Uniform error shape: { status, message, code? }
 *
 * We do NOT cache, retry, or long-poll here. Any of that needs explicit
 * opt-in per call site in the hooks/pages that use this.
 */

/**
 * Resolve the API base URL.
 *
 * In dev with Vite running, VITE_API_BASE isn't actually used for fetches —
 * Vite's proxy forwards /api/* to the dev worker. So we just prefix with ''
 * and let Vite handle it.
 *
 * In production, VITE_API_BASE is the worker URL and we fetch it directly.
 * Cookies are scoped to .tocynid.com so app.tocynid.com → tocynid-api.*.workers.dev
 * works as long as we're on the right domain.
 *
 * We detect dev vs prod by checking import.meta.env.DEV.
 */
function apiUrl(path) {
  if (import.meta.env.DEV) {
    // Vite proxy: fetch relative, proxy forwards to VITE_API_BASE
    return path.startsWith('/') ? path : `/${path}`;
  }
  const base = import.meta.env.VITE_API_BASE || '';
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}

/**
 * Custom error class so call sites can differentiate API errors from
 * network errors. API errors carry HTTP status + server-provided message.
 */
export class ApiError extends Error {
  constructor(status, message, code) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Core fetch wrapper. Returns parsed JSON or throws ApiError.
 *
 * @param {string} path - e.g. '/api/signup'
 * @param {object} opts - fetch options; body is auto-stringified if object
 */
export async function apiFetch(path, opts = {}) {
  const url = apiUrl(path);

  const headers = { ...(opts.headers || {}) };
  let body = opts.body;
  if (body && typeof body === 'object' && !(body instanceof FormData) && !(body instanceof URLSearchParams)) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(body);
  }

  let res;
  try {
    res = await fetch(url, {
      ...opts,
      headers,
      body,
      credentials: 'include',
    });
  } catch (e) {
    // Network error, DNS failure, etc.
    throw new ApiError(0, 'Network error — please check your connection.', 'network');
  }

  const contentType = res.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');

  let payload = null;
  if (isJson) {
    payload = await res.json().catch(() => null);
  }

  if (!res.ok) {
    const message = payload?.error || `Request failed (${res.status})`;
    throw new ApiError(res.status, message, payload?.code);
  }

  return payload;
}

/**
 * Convenience methods.
 */
export const api = {
  get: (path, opts) => apiFetch(path, { method: 'GET', ...(opts || {}) }),
  post: (path, body, opts) => apiFetch(path, { method: 'POST', body, ...(opts || {}) }),
  put: (path, body, opts) => apiFetch(path, { method: 'PUT', body, ...(opts || {}) }),
  del: (path, opts) => apiFetch(path, { method: 'DELETE', ...(opts || {}) }),
};
